import {Component, computed, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {NotificationService} from '../../services/notification.service';
import {PushNotificationService} from '../../services/push-notification.service';
import {RoleInvitationService} from '../../../pages/role-invitations/role-invitation.service';
import {NotificationView} from '../../../../models/generated-models';

/**
 * Header notification bell + dropdown, shared by the desktop and mobile layouts. Reads the
 * app-wide unread count (kept fresh by the layout's existing poll), loads the list on open, and —
 * on first open — offers browser push opt-in (permission + subscribe).
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [NgbDropdown, NgbDropdownMenu, NgbDropdownToggle],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit {
  protected readonly notificationService = inject(NotificationService);
  protected readonly pushService = inject(PushNotificationService);
  protected readonly roleInvitationService = inject(RoleInvitationService);
  private readonly router = inject(Router);

  protected readonly pendingInvitationCount = this.roleInvitationService.pendingCount;

  protected readonly totalBadgeCount = computed(() => {
    return Math.max(this.notificationService.unreadCount(), this.pendingInvitationCount());
  });

  ngOnInit(): void {
    void this.pushService.refresh();
  }

  protected onToggle(open: boolean): void {
    if (open) {
      this.notificationService.load();
      if (this.pushService.isSupported() && !this.pushService.subscribed()) {
        void this.pushService.enable();
      }
    }
  }

  protected markReadOnHover(notification: NotificationView): void {
    if (notification.id != null && !notification.read) {
      this.notificationService.markRead(notification.id);
      notification.read = true;
    }
  }

  protected openRoleInvitations(dropdown?: NgbDropdown): void {
    dropdown?.close();
    void this.router.navigate(['/profile/role-invitations']);
  }

  protected openNotification(notification: NotificationView, dropdown?: NgbDropdown): void {
    dropdown?.close();
    if (notification.id != null && !notification.read) {
      this.notificationService.markRead(notification.id);
    }
    if (notification.type === 'ROLE_ASSIGNMENT') {
      const targetUrl = notification.url
        ? notification.url.replace(/^\/ui/, '')
        : (notification.relatedRecordId != null ? `/profile/role-invitations/${notification.relatedRecordId}` : '/profile/role-invitations');
      void this.router.navigateByUrl(targetUrl);
    } else if (notification.type === 'ROLE_ACCEPTED' || notification.type === 'ROLE_REJECTED') {
      const targetUrl = notification.url ? notification.url.replace(/^\/ui/, '') : '/profile/role-assign';
      void this.router.navigateByUrl(targetUrl);
    } else if (notification.relatedRecordId != null) {
      void this.router.navigate(['/trade', notification.relatedRecordId]);
    } else if (notification.url) {
      void this.router.navigateByUrl(notification.url.replace(/^\/ui/, ''));
    }
  }

  protected markAllRead(event: Event): void {
    event.stopPropagation();
    this.notificationService.markAllRead();
  }
}
